import React, { useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Flex,
  Heading,
  Icon,
  Text,
  Tooltip,
} from '@podiumhq/design-system';

import { usePricingPackagingContext } from 'contexts/PricingPackagingContextProvider';
import { buttonGhostLinkStyles } from 'styles';

import SelectPlanGhostStateCard from '../SelectPlan/components/SelectPlanGhostStateCard';
import { textDashedUnderlineStyles, toolTipStyles } from '../styles';

import BundleCardHeading from './BundleCardHeading';
import BundleCardPrice from './BundleCardPrice';
import ChatWithSalesButton from './ChatWithSalesButton';
import MobileBundleList from './MobileBundleList';

const BundleCard = ({
  bundleName,
  description,
  handleCtaOnClick,
  header,
  highlights,
  onToggleFeatureList,
  popularText,
  subHeader,
}) => {
  const {
    canChoosePlan,
    currentPlan,
    displayPrice,
    getBundlePrices,
    isAnnual,
    isMobileView,
    loadingPrices,
    topHighlightsPackages,
  } = usePricingPackagingContext();
  const [isOpen, setIsOpen] = useState(false);

  const toggleFeatureList = () => {
    setIsOpen(!isOpen);
    onToggleFeatureList && onToggleFeatureList(bundleName);
  };

  return loadingPrices ? (
    <SelectPlanGhostStateCard isMobile={isMobileView} />
  ) : (
    <Flex
      id={`bundle-card-${header.toLowerCase()}`}
      margin={popularText ? '0 32px' : '0'}
      minHeight={isMobileView ? 'auto' : displayPrice ? '580px' : '420px'}
      padding={isMobileView ? '16px' : '32px'}
      {...wrapperStyles}
    >
      <Box mt="8px">
        <BundleCardHeading
          headerText={header}
          popularText={popularText}
          popularTextWrapperProps={{
            borderRadius: '120px',
            height: '36px',
            width: '120px',
            fontSize: '14px',
          }}
        />
      </Box>

      <Text
        color="grey.light"
        fontSize="md"
        fontWeight="normal"
        height="48px"
        mt={displayPrice ? '8px' : '16px'}
      >
        {description}
      </Text>

      {displayPrice && (
        <BundleCardPrice
          align="center"
          mb="16px"
          // savings={isAnnual ? getSavings(bundleName) : null}
          {...getBundlePrices(bundleName)}
        />
      )}

      {isAnnual && (
        <Text color="grey.light" fontSize="sm" fontWeight="normal" mb="16px">
          12 month agreement. Billed monthly.
        </Text>
      )}

      {topHighlightsPackages[bundleName]?.map(
        ({ hovertext, normal, strong } = {}) => (
          <Tooltip key={normal} label={hovertext} {...toolTipStyles}>
            <Box my="8px" {...textDashedUnderlineStyles}>
              <Text as="span" fontSize="md" fontWeight="semibold">
                {strong}
              </Text>
              {normal}
            </Box>
          </Tooltip>
        )
      )}

      <Text fontSize="md" fontStyle="normal" fontWeight="semibold" mt={6}>
        {subHeader}
      </Text>

      <Heading
        mb="16px"
        minHeight={isMobileView ? 'auto' : '180px'}
        {...headingStyles}
      >
        {highlights?.map((highlight) => (
          <Flex
            key={highlight}
            fontSize="md"
            fontWeight="normal"
            lineHeight="23px"
            mt="12px"
          >
            <Box fontSize="16px" mr="8px">
              •
            </Box>
            {highlight}
          </Flex>
        ))}
      </Heading>

      {handleCtaOnClick && (
        <ChatWithSalesButton
          canChoosePlan={canChoosePlan}
          currentBundle={currentPlan === bundleName}
          handleOnClick={handleCtaOnClick}
        />
      )}

      {isMobileView && (
        <>
          <Button onClick={toggleFeatureList} {...toggleLinkDefaultProps}>
            View all features{' '}
            <Icon color="blue.base" type={isOpen ? 'AngleUp' : 'AngleDown'} />
          </Button>

          <Collapse
            animateOpacity
            endingHeight="auto"
            in={isOpen}
            startingHeight={0}
          >
            <MobileBundleList bundleName={bundleName} />
          </Collapse>
        </>
      )}
    </Flex>
  );
};

const headingStyles = {
  fontSize: '2xl',
  fontWeight: 'semibold',
  lineHeight: '40px',
};

const toggleLinkDefaultProps = {
  ...buttonGhostLinkStyles,
  color: 'blue.base',
  display: 'block',
  fontSize: 'md',
  lineHeight: '150%',
  margin: '8px auto 0 auto',
  size: 'md',
};

const wrapperStyles = {
  backgroundColor: 'silver.lightest',
  boxShadow:
    '0 0 0 rgba(18, 21, 26, 0.08), 0 4px 8px rgba(18, 21, 26, 0.04), 0 8px 16px rgba(18, 21, 26, 0.04), 0 16px 32px rgba(18, 21, 26, 0.04)',
  borderRadius: '16px',
  boxSizing: 'border-box',
  direction: 'column',
  justify: 'space-between',
  maxWidth: '360px',
  mb: '40px',
  width: '100%',
};

export default BundleCard;
